
export interface ZonedParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
}

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(zone: string): Intl.DateTimeFormat {
  let fmt = partsFormatters.get(zone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    });
    partsFormatters.set(zone, fmt);
  }
  return fmt;
}

export function zonedPartsOf(instant: Date, zone: string): ZonedParts {
  const parts = partsFormatter(zone).formatToParts(instant);
  const pick = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((p) => p.type === type)?.value;
    return Number(value);
  };
  return {
    year: pick('year'),
    month: pick('month'),
    day: pick('day'),
    hour: pick('hour'),
    minute: pick('minute'),
  };
}

export function zoneOffsetMs(instant: Date, zone: string): number {
  const local = zonedPartsOf(instant, zone);
  const asUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute);
  const instantToMinute = Math.floor(instant.getTime() / 60000) * 60000;
  return asUtc - instantToMinute;
}

export function instantFromZonedParts(parts: ZonedParts, zone: string): Date {
  const guess = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  const offset1 = zoneOffsetMs(new Date(guess), zone);
  const candidate = guess - offset1;
  const offset2 = zoneOffsetMs(new Date(candidate), zone);
  if (offset2 === offset1) return new Date(candidate);
  return new Date(guess - offset2);
}
